import { useEffect, useRef } from 'react';

import { getActiveTrace } from '../experience-trace-id-context';
import { type LabelStack, useInteractionContext } from '../interaction-context';
import { getActiveInteraction, PreviousInteractionLog } from '../interaction-metrics';
import UFORouteName from '../route-name-context';

import { classifyTerminalError, type TerminalErrorCategory } from './classify-terminal-error';

export type { TerminalErrorCategory };

export interface TerminalErrorAdditionalAttributes {
	teamName?: string;
	packageName?: string;
	errorBoundaryId?: string;
	errorHash?: string;
	fallbackType?: 'page' | 'flag' | 'custom';
	statusCode?: number;
}

export interface TerminalErrorData extends TerminalErrorAdditionalAttributes {
	errorType: string;
	errorMessage: string;
	timestamp: number;
	traceId?: string;
	errorCategory?: TerminalErrorCategory;
}

export interface TerminalErrorContext {
	labelStack: LabelStack | null;
	activeInteractionName: string | null;
	activeInteractionId: string | null;
	activeInteractionType: string | null;
	previousInteractionId: string | null;
	previousInteractionName: string | null;
	previousInteractionType: string | null;
	timeSincePreviousInteraction: number | null;
	routeName: string | null;
}

let sinkHandlerFn: (
	errorData: TerminalErrorData,
	context: TerminalErrorContext,
) => void | Promise<void> = () => {};

export function sinkTerminalErrorHandler(
	fn: (errorData: TerminalErrorData, context: TerminalErrorContext) => void | Promise<void>,
): void {
	sinkHandlerFn = fn;
}

const RELAY_NETWORK_ERRORS_NAME = 'RelayNetwork';
/**
 * Relay error structure
 */
type RelayNetworkErrors = {
	name: string;
	type: string;
	source?: {
		errors?: { extensions?: { statusCode?: number } }[];
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isRelayNetworkError = (
	error: RelayNetworkErrors | Error,
): error is RelayNetworkErrors => error.name === RELAY_NETWORK_ERRORS_NAME;

const isErrorObject = (error: unknown): error is Record<string, unknown> =>
	typeof error === 'object' && error !== null;

const getErrorStatusCode = (error: Error) => {
	if (!isErrorObject(error)) {
		return undefined;
	}

	if (isRelayNetworkError(error)) {
		return error.source?.errors?.[0]?.extensions?.statusCode;
	}

	if ('statusCode' in error && typeof error.statusCode === 'number') {
		return error.statusCode;
	}

	return undefined;
};

const getErrorTraceId = (error: Error) =>
	isErrorObject(error) && 'traceId' in error && typeof error.traceId === 'string'
		? error.traceId
		: undefined;

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function setTerminalError(
	error: Error,
	additionalAttributes?: TerminalErrorAdditionalAttributes,
	labelStack?: LabelStack,
): void {
	const activeInteraction = getActiveInteraction();
	const currentTime = performance.now();

	const baseErrorData = {
		errorType: error.name || 'Error',
		errorMessage: error.message?.slice(0, 100) || 'Unknown error',
		timestamp: currentTime,
		errorCategory: classifyTerminalError(error),
	};
	const errorData: TerminalErrorData = {
		...baseErrorData,
		statusCode: additionalAttributes?.statusCode ?? getErrorStatusCode(error),
		// Fallback to traceId from error object if it exists (e.g. FetchError)
		traceId: getActiveTrace()?.traceId ?? getErrorTraceId(error),
		teamName: additionalAttributes?.teamName,
		packageName: additionalAttributes?.packageName,
		errorBoundaryId: additionalAttributes?.errorBoundaryId,
		errorHash: additionalAttributes?.errorHash,
		fallbackType: additionalAttributes?.fallbackType,
	};

	// Calculate time since previous interaction
	const timeSincePreviousInteraction =
		PreviousInteractionLog.timestamp != null
			? currentTime - PreviousInteractionLog.timestamp
			: null;

	const context: TerminalErrorContext = {
		labelStack: labelStack ?? null,
		activeInteractionName: activeInteraction?.ufoName ?? null,
		activeInteractionId: activeInteraction?.id ?? null,
		activeInteractionType: activeInteraction?.type ?? null,
		previousInteractionId: PreviousInteractionLog.id ?? null,
		previousInteractionName: PreviousInteractionLog.name ?? null,
		previousInteractionType: PreviousInteractionLog.type ?? null,
		timeSincePreviousInteraction,
		routeName: UFORouteName.current ?? null,
	};
	sinkHandlerFn(errorData, context);
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function useReportTerminalError(
	error: Error | null | undefined,
	additionalAttributes?: TerminalErrorAdditionalAttributes,
): void {
	const interactionContext = useInteractionContext();
	const hasReportedRef = useRef(false);

	useEffect(() => {
		if (error && !hasReportedRef.current) {
			hasReportedRef.current = true;
			setTerminalError(error, additionalAttributes, interactionContext?.labelStack);
		}
	}, [error, additionalAttributes, interactionContext?.labelStack]);
}
